import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { MapPin, Star, X } from "lucide-react";
import { reviews } from "./reviewsData";

export default function ReviewToast() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * reviews.length)
  );
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(true);
  const [stopped, setStopped] = useState(false);
  const [fadeClass, setFadeClass] = useState("opacity-100");

  const timeoutRef = useRef(null);
  const review = reviews[index];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname === "/mayorista") return;
    if (stopped) return;

    timeoutRef.current = setTimeout(
      () => {
        setFadeClass("opacity-0");
        setTimeout(() => {
          setExpanded(false);

          setIndex((prev) => {
            let next = prev;
            while (reviews.length > 1 && next === prev) {
              next = Math.floor(Math.random() * reviews.length);
            }
            return next;
          });

          setFadeClass("opacity-100");
        }, 400);
      },
      expanded ? 10000 : 7000
    );

    return () => clearTimeout(timeoutRef.current);
  }, [expanded, index, location.pathname, stopped]);

  const handleStop = (e) => {
    e.stopPropagation();
    setStopped(true);
    setVisible(false);
    clearTimeout(timeoutRef.current);
  };

  if (location.pathname === "/mayorista") return null;
  if (location.pathname === "/" && isMobile) return null;
  if (!visible || !review) return null;

  return (
    <div
      className={`fixed bottom-6 left-6 z-[9999] transition-opacity duration-700 ease-in-out ${fadeClass}`}
    >
      <div className="relative bg-gray-900 text-white rounded-2xl shadow-2xl border border-gray-700 overflow-hidden w-[90vw] sm:w-80 md:w-96 max-w-[400px] text-left">
        {/* Close button */}
        <button
          onClick={handleStop}
          aria-label="Cerrar reseña"
          className="absolute top-2 right-2 z-50 text-gray-300 hover:text-white p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <X size={20} />
        </button>

        {/* Image */}
        <img
          src={review.image}
          alt={`Reseña de ${review.name}`}
          className={`object-cover transition-all ${
            expanded ? "h-52 w-full" : "h-20 w-20 float-left"
          } rounded-t-2xl sm:rounded-none`}
          onClick={() => setExpanded(!expanded)}
        />

        {/* Content */}
        <div
          className={`relative p-4 transition-all ${
            expanded
              ? "pt-4 max-h-[70vh] overflow-y-auto"
              : "ml-[88px] max-h-[200px] md:max-h-[240px] overflow-hidden"
          }`}
        >
          {!expanded && (
            <>
              <div className="relative pr-1">
                <p className="font-semibold text-sm sm:text-base">
                  {review.name}
                </p>
                <p className="text-xs sm:text-sm text-gray-300">
                  compartió una reseña:
                </p>
                <div className="flex items-center text-xs sm:text-sm text-gray-400 mt-0.5">
                  <MapPin size={12} className="mr-1" />({review.location})
                </div>
                <p className="mt-1 text-sm sm:text-base font-bold italic leading-tight">
                  “{review.title}”
                </p>
                <p className="text-xs sm:text-sm text-gray-300 mt-1">
                  -{review.product}
                </p>

                {/* Gradient mask */}
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none" />
              </div>

              {/* Always visible “Ver más” */}
              <div className="absolute bottom-2 left-3 z-40">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(true);
                  }}
                  aria-label="Ver más detalles de la reseña"
                  className="text-sm sm:text-base font-semibold text-blue-400 bg-blue-950/40 hover:bg-blue-800/40 px-5 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Ver más
                </button>
              </div>
            </>
          )}

          {expanded && (
            <div className="relative z-10">
              <div className="flex text-yellow-400 mb-2 mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="currentColor" />
                ))}
              </div>
              <h2 className="font-bold italic text-base sm:text-lg mb-1">
                “{review.title}”
              </h2>
              <p className="text-gray-300 text-sm mb-3">{review.comment}</p>

              <div className="text-xs sm:text-sm text-gray-200">
                <p className="font-semibold">{review.name}</p>
                <p className="flex items-center text-gray-400">
                  <MapPin size={12} className="mr-1" /> ({review.location})
                </p>
                <div className="mt-1">
                  <p className="font-semibold text-gray-300">Compra:</p>
                  <p className="text-gray-400">{review.product}</p>
                </div>
              </div>

              <div className="pt-4 text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(false);
                  }}
                  aria-label="Cerrar detalles de la reseña"
                  className="text-sm sm:text-base font-semibold text-blue-400 bg-blue-950/40 hover:bg-blue-800/40 px-5 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Ver menos
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
